import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';

export function TermbaseAdmin({ api, onChanged }) {
    const [archived, setArchived] = useState(false);
    const [rows, setRows] = useState([]);
    const [contributor, setContributor] = useState('all');
    const [selected, setSelected] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const load = async () => {
        try {
            const response = await api.get('/admin/terms', { params: { archived } });
            setRows(response.data); setSelected([]); setError('');
        } catch (e) { setError(e.response?.data?.message || 'Could not load administration records.'); }
    };
    useEffect(() => { load(); }, [archived]); // eslint-disable-line react-hooks/exhaustive-deps
    const contributors = useMemo(() => [...new Set(rows.map(r => r.createdBy || 'legacy'))].sort(), [rows]);
    const visible = rows.filter(r => contributor === 'all' || (r.createdBy || 'legacy') === contributor);
    const chosen = visible.filter(r => selected.includes(r._id));
    const run = async () => {
        if (!chosen.length || busy) return;
        const action = archived ? 'restore' : 'archive';
        if (!window.confirm(`${action === 'archive' ? 'Archive' : 'Restore'} ${chosen.length} selected terms?`)) return;
        setBusy(true);
        try {
            // Limit each operation to 500 entries; all remain recoverable.
            for (let i = 0; i < chosen.length; i += 500)
                await api.post(`/admin/terms/${action}`, { ids: chosen.slice(i, i + 500).map(r => r._id) });
            await load(); await onChanged();
        } catch (e) { setError(e.response?.data?.message || 'Operation failed.'); }
        finally { setBusy(false); }
    };
    return <Paper elevation={0} sx={{ mb: 3, p: 3, border: '1px solid #E3E9F3', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={750} mb={1}>Termbase administration</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>Review contributors and archive terms safely. Archived terms are excluded from normal search and matching.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <Button variant={archived ? 'outlined' : 'contained'} onClick={() => { setArchived(false); setContributor('all'); }}>Active</Button>
            <Button variant={archived ? 'contained' : 'outlined'} onClick={() => { setArchived(true); setContributor('all'); }}>Archive</Button>
            <FormControl size="small" sx={{ minWidth: 220 }}><InputLabel>Contributor</InputLabel><Select label="Contributor" value={contributor} onChange={e => { setContributor(e.target.value); setSelected([]); }}>
                <MenuItem value="all">All contributors</MenuItem>
                {contributors.map(c => <MenuItem key={c} value={c}>{c === 'legacy' ? 'Unknown / Legacy' : c}</MenuItem>)}
            </Select></FormControl>
            <Button variant="outlined" onClick={load}>Refresh</Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="body2">{visible.length} shown (maximum 5,000 per view) · {chosen.length} selected</Typography>
            <Button disabled={!chosen.length || busy} color={archived ? 'primary' : 'warning'} variant="contained" onClick={run}>{archived ? 'Restore selected' : 'Archive selected'}</Button>
        </Stack>
        <Box sx={{ maxHeight: 340, overflowY: 'auto', borderTop: '1px solid #E3E9F3' }}>
            <Stack direction="row" alignItems="center"><Checkbox checked={visible.length > 0 && visible.every(r => selected.includes(r._id))} onChange={e => setSelected(e.target.checked ? visible.map(r => r._id) : [])}/><Typography variant="caption">Select all displayed</Typography></Stack>
            {visible.map(r => <Stack key={r._id} direction="row" alignItems="center" sx={{ borderTop: '1px solid #EDF0F5', py: 0.5 }}>
                <Checkbox checked={selected.includes(r._id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, r._id] : prev.filter(id => id !== r._id))}/>
                <Box><Typography variant="body2" fontWeight={600}>{r.translations?.en || r.productName || 'Unnamed term'}</Typography><Typography variant="caption" color="text.secondary">{r.createdByEmail || r.createdBy || 'Unknown / Legacy'} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'Date unknown'}{r.importBatchId ? ` · Import ${r.importBatchId.slice(0, 8)}` : ''}</Typography></Box>
            </Stack>)}
        </Box>
    </Paper>;
}
